import { useNavigate, useParams } from "react-router-dom";
import { CirculationSchema, ICirculationSettings } from "../api/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import MainCard from "components/MainCard";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import { showError, showSuccess } from "utils/showMessages";
import { showServerErrors } from "utils/formUtils";
import { ApiResponse } from "common/models/ApiResponse";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "components/Loader";
import { UpdateCirculationSettings } from "../api/mutation";
import { GetCirculationSettings } from "../api/hooks";

const CirculationSettingsTab = () => {
  const { libraryID } = useParams();
  const editMode = libraryID !== undefined;
  const queryClient = useQueryClient();

  const navigateTo = useNavigate();
  const updateMutation = UpdateCirculationSettings();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ICirculationSettings>({
    resolver: zodResolver(CirculationSchema),
  });

  const getCirculationSettingsQuery = GetCirculationSettings(
    libraryID || "",
  );

  // const [circulationSettings, setCirculationSettings] =
  //   useState<ICirculationSettings>();

  useEffect(() => {
    if (getCirculationSettingsQuery?.data) {
      // setCirculationSettings(getCirculationSettingsQuery.data.apiData);
      reset(getCirculationSettingsQuery.data.apiData);
    }
  }, [getCirculationSettingsQuery.data, reset]);

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    data.libraryID = libraryID || "";

    updateMutation.mutate(data, {
      onSettled(response) {
        handleResponse(response);
      },
    });
  });

  const handleResponse = async (response: ApiResponse<number> | undefined) => {
    if (response?.status == 200) {
      if (editMode) {
        queryClient.invalidateQueries({
          queryKey: [
            "library",
            "getCirculationSettings",
            "CirculationSettings",
          ],
        });
      }
      showSuccess(`Record ${editMode ? "updated" : "added"} successfully`);
      navigateTo(`../edit/${libraryID}/circulation`);
    } else {
      if (response?.status == 404) {
        showError(response?.title ?? "");
      } else if (response?.status == 400) {
        if (response?.modelErrors) {
          showServerErrors(setError, response.errors);
          showError(response?.title ?? "");
        } else {
          showError(response?.title ?? "");
        }
      } else {
        showError(response?.title ?? "");
      }
    }
  };

  return (
    <>
      <MainCard
        content={false}
        darkTitle
        darktitleVariant={"h4"}
        title={editMode ? "Circulation Settings" : "Add Library"}
      >
        <Box component={"form"} sx={{ p: 2.5 }} onSubmit={onSubmit}>
          {getCirculationSettingsQuery.isFetching ? <Loader /> : <></>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="issueMaxItems" sx={{ mb: "-5px" }}>
                Issue Max Items <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.issueMaxItems}>
                <TextField
                  placeholder="Enter Issue Max Items"
                  {...register("issueMaxItems")}
                  id="issueMaxItems"
                  margin="normal"
                  error={!!errors.issueMaxItems}
                  helperText={
                    errors.issueMaxItems ? errors.issueMaxItems?.message : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="issueMaxDays" sx={{ mb: "-5px" }}>
                Issue Max Days <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.issueMaxDays}>
                <TextField
                  placeholder="Enter Issue Max Days"
                  {...register("issueMaxDays")}
                  id="issueMaxDays"
                  fullWidth
                  margin="normal"
                  error={!!errors.issueMaxDays}
                  helperText={
                    errors.issueMaxDays ? errors.issueMaxDays?.message : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="reserveMaxItems" sx={{ mb: "-5px" }}>
                Reserve Max Items <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.reserveMaxItems}>
                <TextField
                  placeholder="Enter Reserve Max Items"
                  {...register("reserveMaxItems")}
                  id="reserveMaxItems"
                  margin="normal"
                  error={!!errors.reserveMaxItems}
                  helperText={
                    errors.reserveMaxItems
                      ? errors.reserveMaxItems?.message
                      : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="reserveMaxDays" sx={{ mb: "-5px" }}>
                Reserve Max Days <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.reserveMaxDays}>
                <TextField
                  placeholder="Enter Reserve Max Days"
                  {...register("reserveMaxDays")}
                  id="reserveMaxDays"
                  margin="normal"
                  error={!!errors.reserveMaxDays}
                  helperText={
                    errors.reserveMaxDays ? errors.reserveMaxDays?.message : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item sm={12}>
              <Button
                title={editMode ? "Update" : "Save"}
                type="submit"
                disabled={isSubmitting}
                variant="contained"
              >
                {isSubmitting && (
                  <CircularProgress
                    color="inherit"
                    size="1rem"
                    sx={{ mr: 1 }}
                  />
                )}
                {editMode ? "Update" : "Save"}
              </Button>
              <Button
                title="Back"
                sx={{ ml: 1 }}
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => navigateTo(`/admin/library`)}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Box>
      </MainCard>
    </>
  );
};
export default CirculationSettingsTab;
